import { useQuery } from "react-query";
import type { ApiError } from "@/react/config/CustomErrors";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";

export const GOOGLE_FONTS_QUERY_KEY = ["google-fonts"];
// cspell:disable-next-line -- This is an API key for Google Fonts
const GOOGLE_FONTS_API_KEY = "AIzaSyAf-__UKJbHC8ObQ1UEoTwWemvhnKwBG6U";

/**
 * Filters out variants that are a font weight + style combination.
 */
const filterBasicVariants = (variants: string[]): string[] =>
  variants.filter(variant => {
    const hasWeightAndStyle =
      /^\d+(italic|bold|black|light|medium|thin|extra|semi|ultra)$/i.test(
        variant,
      );
    const isBasicWeight = /^\d+$/.test(variant);
    const isRegular = variant === "regular";

    return isRegular || (isBasicWeight && !hasWeightAndStyle);
  });

export type Variants = string[];

interface GoogleFont {
  family: string;
  variants: Variants;
}

export interface GoogleFontOptions {
  label: string;
  value: string;
}

interface GoogleFontResponse {
  fonts: GoogleFont[];
  options: GoogleFontOptions[];
}

interface RawGoogleFontData {
  category: string;
  family: string;
  files: Record<string, string>;
  kind: string;
  lastModified: string;
  menu: string;
  subsets: string[];
  variants: Variants;
  version: string;
}

interface GoogleFontsResponse {
  items: RawGoogleFontData[];
}

export const useListGoogleFonts = () =>
  useQuery<GoogleFontsResponse, ApiError, GoogleFontResponse>({
    queryKey: GOOGLE_FONTS_QUERY_KEY,
    queryFn: () =>
      reactQueryGet(
        `https://www.googleapis.com/webfonts/v1/webfonts?key=${GOOGLE_FONTS_API_KEY}&sort=popularity`,
      ),
    select: (data: GoogleFontsResponse) => {
      const fonts = data.items.map(font => ({
        family: font.family,
        variants: filterBasicVariants(font.variants),
      }));

      const options = fonts.map(font => ({
        label: font.family,
        value: font.family,
      }));

      return { fonts, options };
    },
    staleTime: 1000 * 60 * 60 * 24 * 7,
  });
